import { useEffect, useState } from 'react';

/**
 * Rediseño · Bloque B — los tres anchos del shell y los dos cortes que los separan.
 *
 * Los cortes salen del diseño aprobado (P-030) y se justifican por el contenido, no por un equipo:
 *
 *   compact  (< 768)       No cabe un menú lateral junto al contenido: barra inferior con las
 *                          pestañas aprobadas y el menú completo como capa («Más»). Aquí caen el
 *                          Scangle SP01 y cualquier teléfono, del más angosto al más ancho, también
 *                          apaisado.
 *   medium   (768 – 1099)  Cabe un riel de iconos (76 px) y el contenido conserva al menos los
 *                          692 px que su maquetación de dos columnas necesita; el menú completo
 *                          (260 px) todavía lo dejaría por debajo de eso.
 *   wide     (≥ 1100)      Menú completo (260 px) y contenido de 840 px o más.
 *
 * El ancho que se mide es el de la ventana, en px de CSS. Ningún corte depende del modelo ni del
 * user-agent: el ancho efectivo del Scangle no está registrado (FI-51) y no hace falta conocerlo —
 * sea 360 o 480, es «compact».
 *
 * `shell.css` repite estos dos números en sus media queries (una media query no puede leer una
 * variable); `shell.test.ts` comprueba que coincidan.
 */
export const SHELL_BREAKPOINTS = { medium: 768, wide: 1100 } as const;

export type ShellTier = 'compact' | 'medium' | 'wide';

export function resolveShellTier(width: number): ShellTier {
  if (width >= SHELL_BREAKPOINTS.wide) return 'wide';
  if (width >= SHELL_BREAKPOINTS.medium) return 'medium';
  return 'compact';
}

function currentTier(): ShellTier {
  return typeof window === 'undefined' ? 'wide' : resolveShellTier(window.innerWidth);
}

/**
 * El ancho actual del shell. La apariencia de cada ancho la decide `shell.css`; esto existe para lo
 * que el CSS no puede hacer — cerrar el menú abierto como capa cuando la ventana cambia de ancho.
 */
export function useShellTier(): ShellTier {
  const [tier, setTier] = useState<ShellTier>(currentTier);

  useEffect(() => {
    const update = () => setTier(currentTier());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return tier;
}
